import React, { useState } from "react";
import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  DropdownFilter,
  LeadsRadialPieChart,
  metricsToRadialSlices,
  TABLE_MIN_HEIGHT,
  TableFooter,
} from "../../../common";
import {
  useLeadPageInteractionsChartQuery,
  useLeadPageInteractionsQuery,
} from "src/query/experiences/leadsAndReports/leads";
import { convertDatesToTimezone, dateKey } from "src/services/experiences/leadsAndReports/dateUtils";
import { isInteractive } from "../../../constants";
import { formatDurationWithLabel } from "../../../utils/format";
import { ArrangeOption } from "../../../common/DropdownFilter";
import { ChartView, ReportDateFilter } from "src/types/experiences/leadsAndReports";
import styles from "../../../common/common.module.scss";

interface PageInteractionsProps {
  storyboardId: string;
  profileId: string;
  dateFilter: ReportDateFilter | null;
  storyboardType?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "name", header: "Page Name" },
  { key: "views", header: "Views", align: "right" },
  { key: "time", header: "Total Time", align: "right" },
  { key: "completed", header: "% Completed", align: "right" },
];

const PIE_OPTIONS = [
  { label: "Views", value: "views" },
  { label: "Time", value: "timeSpent" },
];

export const PageInteractions: React.FC<PageInteractionsProps> = ({
  storyboardId,
  profileId,
  dateFilter,
  storyboardType,
}) => {
  const [page, setPage] = useState(0);
  const [chart, setChart] = useState<ChartView>("table_view");
  const [viewOption, setViewOption] = useState<ArrangeOption>({
    label: "Views",
    value: "views",
    name: "name",
  });
  const typeReady = storyboardType !== undefined;
  const variant = isInteractive(storyboardType) ? "interactive" : "landing";
  const request = { storyboard: storyboardId, ...convertDatesToTimezone(dateFilter ?? undefined) };
  const { data, isLoading } = useLeadPageInteractionsQuery(
    profileId,
    request,
    variant,
    dateKey(dateFilter ?? undefined),
    page,
    Boolean(dateFilter) && typeReady,
  );
  const rows = typeReady ? (data?.records ?? []) : [];

  // Pie chart uses the dedicated top-6 dataset, not the current table page.
  const { data: chartData, isLoading: isChartLoading } = useLeadPageInteractionsChartQuery(
    profileId,
    request,
    variant,
    dateKey(dateFilter ?? undefined),
    Boolean(dateFilter) && typeReady && chart === "pie_chart_view",
  );
  const pieData = chartData?.records ?? [];

  const renderChart = () => {
    switch (chart) {
      case "pie_chart_view":
        return (
          <div className={styles.chartFull}>
            <LeadsRadialPieChart
              data={metricsToRadialSlices(pieData, viewOption)}
              measureLabel={viewOption.label}
              height={240}
              loading={isChartLoading}
            />
          </div>
        );
      case "table_view":
        return (
          <AnalyticsTable
            variant="split"
            columns={COLUMNS}
            rows={rows}
            loading={isLoading || !typeReady}
            renderRow={(row: any) => (
              <>
                <Cell headline={row.name} />
                <Cell headline={row.views} />
                <Cell headline={formatDurationWithLabel(row.timeSpent).value} />
                <Cell headline={`${Math.round(row.percentageCompleted)}%`} />
              </>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Page Interactions"
      count={typeReady ? (data?.totalRecordsCount ?? rows.length) : undefined}
      currentChart={chart}
      disableView={["graph_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.split}
      action={
        chart === "pie_chart_view" ? (
          <DropdownFilter
            arrangeOption={viewOption}
            handleArrange={(o) => setViewOption((s) => ({ ...s, ...o }))}
            disableOption={[]}
            options={PIE_OPTIONS}
          />
        ) : undefined
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Pages"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
